import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { Crown, Download, Share2, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

export const CrownCertificate = ({ score, label, name, date }) => {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const render = async () => {
    const canvas = await html2canvas(cardRef.current, {
      backgroundColor: "#0A0A0A",
      scale: 2,
      useCORS: true,
      logging: false,
    });
    return canvas;
  };

  const download = async () => {
    setBusy(true);
    try {
      const canvas = await render();
      const link = document.createElement("a");
      link.download = `carlocs-crown-score-${score}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Certificate downloaded.");
    } catch {
      toast.error("Could not generate the image.");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    setBusy(true);
    try {
      const canvas = await render();
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], `carlocs-crown-score-${score}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My Carlocs Crown Score",
          text: `My scalp Crown Score is ${score}/100 — ${label}. #Carlocs` });
      } else {
        await download();
      }
    } catch {
      /* user cancelled share */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex justify-center">
        <div
          ref={cardRef}
          style={{ width: 380, height: 380 }}
          className="relative flex flex-col items-center justify-center overflow-hidden rounded-2xl border border-gold/40 bg-charcoal p-8 text-center"
          data-testid="crown-certificate"
        >
          <div className="absolute inset-0 opacity-10" style={{ background: "radial-gradient(circle at 50% 0%, #D4AF37, transparent 60%)" }} />
          <img src="/carlocs-logo-192.png" alt="Carlocs" crossOrigin="anonymous" className="mb-3 h-14 w-14 object-contain" />
          <span className="text-[10px] uppercase tracking-[0.35em] text-gold">Carlocs Scalp Analyzer</span>
          <span style={{ fontFamily: "'Cormorant Garamond', serif" }} className="mt-6 block text-[110px] font-semibold leading-none text-cream">{score}</span>
          <span className="mt-1 text-sm text-muted-foreground">/ 100 Crown Score</span>
          <span style={{ fontFamily: "'Cormorant Garamond', serif" }} className="mt-4 text-3xl text-gold">{label}</span>
          <div className="mt-6 border-t border-charcoal-border pt-4">
            <p className="text-sm text-cream">{name}</p>
            <p className="text-[11px] text-muted-foreground">{date}</p>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-center gap-3">
        <Button onClick={download} disabled={busy} className="rounded-full bg-gold text-charcoal hover:bg-gold/90" data-testid="cert-download-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="mr-2 h-4 w-4" /> Download</>}
        </Button>
        <Button onClick={share} disabled={busy} variant="ghost" className="rounded-full border border-charcoal-border text-cream hover:text-gold" data-testid="cert-share-btn">
          <Share2 className="mr-2 h-4 w-4" /> Share
        </Button>
      </div>
    </div>
  );
};
